/**
 * Management Strategy — two blocks, one card.
 *
 * Easy Yes/No and Needs a look used to sit side by side and push Ad health
 * off the screen. One CollapsibleCard (same as the rest of Management) plus
 * tabs so only one side is open. Each tab still scrolls inside a max height
 * so a long Easy list cannot bury Ad health. Budget Yes stays one package.
 *
 * Read failures sit above the tabs. Radix unmounts the inactive tab, and
 * conversions has no Easy-side incomplete list — a failed conversion read
 * would otherwise look like a clean board.
 */
import { useEffect, useState } from 'react';
import { Scale, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { CollapsibleCard } from '@/components/google-ads/CollapsibleCard';
import { BudgetStrategyPanel } from '@/components/google-ads/BudgetStrategyPanel';
import { StrategyDeepDive } from '@/components/google-ads/StrategyDeepDive';
import { StrategyEasyQueue } from '@/components/google-ads/StrategyEasyQueue';
import {
  collectReadFailures,
  lookListCount,
  strategyEasyReadsOpen,
} from '@/components/google-ads/strategyBoardModel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { listStrategyGoogleRecs } from '@/services/googleAdsManagementService';
import { reviewSearchTermsNegatives } from '@/services/googleAdsWeeklyService';
import type {
  AdMonitorResponse,
  ConversionViewResponse,
  StructureResponse,
} from '@/services/googleAdsManagementService';

const TAB_STORAGE = 'ga-mgmt-strategy-tab';

/** Cap so a long Easy list cannot bury Ad health. Taller than the old
 *  two-column board because the findings table now carries search / filter
 *  chrome above the rows. */
const TAB_SCROLL =
  'max-h-[26rem] min-h-0 overflow-y-auto overscroll-contain lg:max-h-[min(32rem,52vh)]';

function readStoredTab(): 'easy' | 'look' {
  try {
    const raw = window.localStorage.getItem(TAB_STORAGE);
    return raw === 'look' ? 'look' : 'easy';
  } catch {
    return 'easy';
  }
}

export function StrategyBoard({
  tenantId,
  ads,
  conversions,
  structure,
  adsLoading = false,
  structureLoading = false,
  conversionsLoading = false,
  adsError = false,
  structureError = false,
  conversionsError = false,
}: {
  tenantId: string;
  ads?: AdMonitorResponse;
  conversions?: ConversionViewResponse;
  structure?: StructureResponse;
  adsLoading?: boolean;
  structureLoading?: boolean;
  conversionsLoading?: boolean;
  adsError?: boolean;
  structureError?: boolean;
  conversionsError?: boolean;
}) {
  // ⛔ VIKTOR (#2248 review): computed once here, from all three query
  // states AND all three query-error flags, and threaded down to
  // StrategyDeepDive as a prop instead of it recomputing independently —
  // "in one place" so the banner, the tab count, and the deep-dive empty
  // state can never disagree about whether a read failed.
  const failures = collectReadFailures({
    ads,
    conversions,
    structure,
    adsError,
    conversionsError,
    structureError,
  });
  const googleRecs = useQuery({
    queryKey: ['google-ads-strategy-google-recs', tenantId],
    queryFn: () => listStrategyGoogleRecs(tenantId),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
  const searchTerms = useQuery({
    queryKey: ['google-ads-strategy-search-terms', tenantId],
    queryFn: () => reviewSearchTermsNegatives({ tenantId }),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  });
  const googleRecItems = googleRecs.data?.read_ok ? googleRecs.data.items : [];
  const googleRecsFailed =
    googleRecs.isError || Boolean(googleRecs.data && !googleRecs.data.read_ok);
  const allTermRecs = searchTerms.isSuccess
    ? searchTerms.data.recommendations
    : undefined;
  const easyReadsOpen =
    strategyEasyReadsOpen({
      adsFailed: Boolean(ads && !ads.read_ok) || adsError,
      structureFailed: Boolean(structure && !structure.read_ok) || structureError,
      searchTermsFailed: searchTerms.isError,
    }) &&
    !adsLoading &&
    !structureLoading &&
    !searchTerms.isLoading;
  const lookCount =
    lookListCount({
      ads,
      conversions,
      structure,
      googleRecs: googleRecItems,
      searchTermRecs: allTermRecs,
      easyReadsOpen,
    }) +
    failures.length +
    (googleRecsFailed ? 1 : 0);
  const [tab, setTab] = useState<'easy' | 'look'>('easy');

  useEffect(() => {
    setTab(readStoredTab());
  }, []);

  return (
    <CollapsibleCard
      id="strategy"
      title="Account audit"
      summary="Findings · Yes or No"
      description="This is an audit of the account. Easy Yes/No is search-term excludes and sibling-safe pauses. Pause is this window, not a lifetime verdict — re-enable later if it should run again. Needs a look is Google recs, hygiene, or pause-versus-restructure."
    >
      {failures.length > 0 ? (
        <div className="mb-3 divide-y divide-border">
          {failures.map((failure) => (
            <div
              key={failure.source}
              className="border border-coral/40 bg-coral-light/20 px-4 py-3 first:rounded-t-md last:rounded-b-md"
            >
              <p className="text-sm font-medium text-coral-dark">
                {failure.message}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                This list is incomplete, not empty. A failed {failure.source}{' '}
                read is not &quot;nothing to look at.&quot;
              </p>
            </div>
          ))}
        </div>
      ) : null}
      <Tabs
        value={tab}
        onValueChange={(next) => {
          const value = next === 'look' ? 'look' : 'easy';
          setTab(value);
          try {
            window.localStorage.setItem(TAB_STORAGE, value);
          } catch {
            // Tab preference is convenience only.
          }
        }}
      >
        <TabsList className="grid h-auto w-full grid-cols-2">
          <TabsTrigger value="easy" className="gap-1.5">
            <Scale className="h-3.5 w-3.5" />
            Easy Yes / No
          </TabsTrigger>
          <TabsTrigger value="look" className="gap-1.5">
            <Search className="h-3.5 w-3.5" />
            Needs a look
            {lookCount > 0 ? (
              <span className="text-xs font-normal text-muted-foreground">
                {lookCount}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="easy" className="mt-3">
          <div className={TAB_SCROLL} aria-label="Easy Yes / No list">
            <BudgetStrategyPanel tenantId={tenantId} embedded />
            <div className="mt-3 border-t border-border">
              <p className="px-1 pt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Findings
              </p>
              <StrategyEasyQueue
                tenantId={tenantId}
                ads={ads}
                structure={structure}
                adsLoading={adsLoading}
                structureLoading={structureLoading}
                adsError={adsError}
                structureError={structureError}
              />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="look" className="mt-3">
          <div className={TAB_SCROLL} aria-label="Needs a look list">
            <StrategyDeepDive
              ads={ads}
              conversions={conversions}
              structure={structure}
              failures={failures}
              readsPending={adsLoading || structureLoading || conversionsLoading}
              googleRecs={googleRecItems}
              googleRecsFailed={googleRecsFailed}
              googleRecsMessage={googleRecs.data?.message}
              googleRecsTruncated={Boolean(googleRecs.data?.truncated)}
              googleRecsShown={googleRecItems.length}
              googleRecsTotal={googleRecs.data?.total ?? 0}
              searchTermRecs={allTermRecs}
              easyReadsOpen={easyReadsOpen}
            />
          </div>
        </TabsContent>
      </Tabs>
    </CollapsibleCard>
  );
}
